/**
 * Central locale resolution: missing strings in a language pack fall back to German (`de`).
 */

function isPlainObject(x) {
  return x != null && typeof x === "object" && !Array.isArray(x);
}

export function isNonEmptyLocaleString(value) {
  return value != null && String(value).trim() !== "";
}

/**
 * @param {unknown} obj
 * @param {string} path Dot-separated path (e.g. `meter.cta`).
 */
export function getByLocalePath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).split(".").filter(Boolean);
  let cur = obj;
  for (const part of parts) {
    if (!isPlainObject(cur) || !(part in cur)) return undefined;
    cur = cur[part];
  }
  return cur;
}

/**
 * Deep-merge `override` onto a clone of `base` (override wins on leaf conflicts).
 * @param {Record<string, unknown>} base
 * @param {Record<string, unknown>} override
 */
function shouldOverlayLocaleLeaf(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function deepMergeLocale(base, override) {
  if (!isPlainObject(override)) return base;
  const out = { ...base };
  for (const key of Object.keys(override)) {
    const sv = override[key];
    if (isPlainObject(sv)) {
      const tv = out[key];
      if (!isPlainObject(tv)) out[key] = {};
      out[key] = deepMergeLocale(/** @type {Record<string, unknown>} */ (out[key]), sv);
    } else if (shouldOverlayLocaleLeaf(sv)) {
      out[key] = sv;
    }
  }
  return out;
}

/**
 * Language pack with German fill-in for missing keys.
 * @param {Record<string, unknown> | null | undefined} langPack
 * @param {Record<string, unknown>} dePack
 */
export function mergeLocalePack(langPack, dePack) {
  if (!dePack || !Object.keys(dePack).length) return langPack || {};
  if (!langPack || langPack === dePack) return dePack;
  return deepMergeLocale(JSON.parse(JSON.stringify(dePack)), langPack);
}

/**
 * Deep-merge `overlay` onto `base`. Keys present in `overlay` win even when the
 * value is an empty string (used for UI packs where "" means “omit this fragment”).
 * @param {Record<string, unknown>} base
 * @param {Record<string, unknown>} overlay
 */
export function applyExplicitLocaleOverlay(base, overlay) {
  if (!isPlainObject(base)) {
    return isPlainObject(overlay) ? { ...overlay } : overlay;
  }
  if (!isPlainObject(overlay)) return { ...base };
  const out = { ...base };
  for (const key of Object.keys(overlay)) {
    const sv = overlay[key];
    if (isPlainObject(sv)) {
      out[key] = applyExplicitLocaleOverlay(
        isPlainObject(out[key]) ? /** @type {Record<string, unknown>} */ (out[key]) : {},
        sv,
      );
    } else if (Object.prototype.hasOwnProperty.call(overlay, key)) {
      out[key] = sv;
    }
  }
  return out;
}

/**
 * Pick a dotted path from `primary`, then from `german` if empty.
 * @param {Record<string, unknown> | null | undefined} primary
 * @param {string} path
 * @param {Record<string, unknown> | null | undefined} [german]
 */
export function pickLocalePath(primary, path, german) {
  const direct = getByLocalePath(primary, path);
  if (isNonEmptyLocaleString(direct)) return String(direct).trim();
  if (german) {
    const deVal = getByLocalePath(german, path);
    if (isNonEmptyLocaleString(deVal)) return String(deVal).trim();
  }
  return "";
}

/**
 * Pick a leaf key from a flat or nested locale object (checks `common` on primary).
 * @param {Record<string, unknown> | null | undefined} primary
 * @param {string} key
 * @param {Record<string, unknown> | null | undefined} [german]
 */
export function pickLocaleLeaf(primary, key, german) {
  if (!key) return "";
  if (primary && isNonEmptyLocaleString(primary[key])) return String(primary[key]).trim();
  const common = primary && primary.common;
  if (isPlainObject(common) && isNonEmptyLocaleString(common[key])) return String(common[key]).trim();
  if (german) {
    if (isNonEmptyLocaleString(german[key])) return String(german[key]).trim();
    const deCommon = german.common;
    if (isPlainObject(deCommon) && isNonEmptyLocaleString(deCommon[key])) return String(deCommon[key]).trim();
  }
  return "";
}

/**
 * @param {string} template
 * @param {Record<string, string>} [vars]
 */
export function formatLocaleTemplate(template, vars) {
  let out = String(template || "");
  if (!vars) return out;
  for (const [name, value] of Object.entries(vars)) {
    out = out.split(`{${name}}`).join(String(value ?? ""));
  }
  return out;
}

/**
 * Row-oriented catalog (`{ key: { de, en, … } }`): prefer UI lang, then `de`.
 * @param {Record<string, Record<string, string>> | null | undefined} catalog
 * @param {string} key
 * @param {string} uiLang
 */
export function pickCatalogRow(catalog, key, uiLang) {
  const row = catalog && catalog[key];
  if (!row || typeof row !== "object") return "";
  const lang = String(uiLang || "de").trim().toLowerCase().split(/[-_]/)[0];
  if (lang === "ch") {
    if (isNonEmptyLocaleString(row.de)) return String(row.de).trim();
  } else if (isNonEmptyLocaleString(row[lang])) {
    return String(row[lang]).trim();
  }
  if (isNonEmptyLocaleString(row.de)) return String(row.de).trim();
  return "";
}
